import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonIconProps } from '@components/button/button.type';
import { ButtonIcon } from '@components/button/button-icon';
import { BUTTON_VARIANT_CLEAN, BUTTON_VARIANT_S } from '@components/button/config';
import { IconDotsVertical } from '@components/icons/icon-dots-vertical';
import { MD_VIEWPORT } from '@constants/viewport';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { useIsTouchDevice } from '@hooks/use-is-touch-device';
import { useWindowSize } from '@hooks/use-window-size';
import classNames from 'classnames';

import { MobileSubmenuIcon } from './components/mobile-submenu-icon';
import type { Actions } from './submenu-icon.type';
import { SubmenuIconButton } from './submenu-icon-button';
import { SubmenuIconLink } from './submenu-icon-link';

export const SubmenuIcon = ({
  options,
  buttonContent,
  className,
  buttonClasses,
  size = BUTTON_VARIANT_S,
  variant = BUTTON_VARIANT_CLEAN,
  zIndexClassName = 'z-50',
  label,
  disabled = false,
}: {
  buttonContent?: React.ReactNode;
  options: Actions[];
  className?: string;
  buttonClasses?: string;
  size?: NonNullable<ButtonIconProps['size']>;
  variant?: NonNullable<ButtonIconProps['variant']>;
  zIndexClassName?: string;
  label?: string;
  disabled?: boolean;
}) => {
  const isMobileViewPort = useWindowSize(MD_VIEWPORT as number);
  const isTouchDevice = useIsTouchDevice();
  const { t } = useTranslation();
  const resolvedLabel = label ?? t('common:optionsLabel');

  // Use the mobile bottom-sheet variant on touch-primary devices (e.g. iPad) as well,
  // since the desktop dropdown nests a <button> inside an <a>/<button> card wrapper,
  // which iPadOS Safari routes to the wrong target on touch (dropdown fails to open).
  const shouldUseMobileMenu = isMobileViewPort || isTouchDevice;

  if (disabled) {
    return (
      <div className={classNames('flex relative', className)} data-testid='submenu'>
        <ButtonIcon
          Icon={IconDotsVertical}
          size={size}
          variant={variant}
          disabled
          aria-label={resolvedLabel}
          dataTestId='submenu-trigger'
          className={buttonClasses}
        />
      </div>
    );
  }

  if (shouldUseMobileMenu) {
    return (
      <MobileSubmenuIcon
        options={options}
        buttonContent={buttonContent}
        buttonClasses={buttonClasses}
        size={size}
        variant={variant}
        label={resolvedLabel}
      />
    );
  }
  return (
    <Menu as='div' className={classNames('flex relative', className)} data-testid='submenu'>
      {({ close, open }) => (
        <div className='relative'>
          <MenuButton
            aria-label={resolvedLabel}
            data-testid='submenu-trigger'
            className={classNames(buttonClasses)}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.preventDefault();
              e.stopPropagation();
            }}>
            {buttonContent ?? <ButtonIcon as='div' Icon={IconDotsVertical} size={size} variant={variant} />}
          </MenuButton>
          <Transition
            show={open}
            as={Fragment}
            appear={true}
            enter='transition ease-out duration-100'
            enterFrom='opacity-0 scale-95'
            enterTo='opacity-100 scale-100'
            leave='transition ease-in duration-75'
            leaveFrom='opacity-100 scale-100'
            leaveTo='opacity-0 scale-95'>
            {/* The panel is already portaled to document.body — Headless UI v2 forces
                `portal=true` whenever `anchor` is set. Without an explicit z-index it landed at
                z-auto and lost the stacking battle to the sticky MenuTop (z-20). Default z-50
                (matches the mobile bottom-sheet variant) puts it above MenuTop and Modal v1/v2
                panels (z-40); callers can pass a higher value via `zIndexClassName` when the
                dropdown needs to clear something above z-50 (e.g. modals portaled over the DS
                Drawer using `DRAWER_OVERLAY_Z_INDEX`, see `@constants/z-index`). */}
            <MenuItems
              modal={false}
              anchor={{ to: 'bottom end', gap: 0 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              className={classNames(
                'w-56 flex flex-col gap-2 p-3 bg-primary rounded-lg shadow-[2px_2px_20px_0_rgba(0,0,0,0.25)] overflow-auto focus:outline-hidden',
                zIndexClassName
              )}>
              <p className='text-left text-body-xs mb-2 text-secondary'>{resolvedLabel}</p>
              {options.map((option) => (
                <MenuItem key={option.name}>
                  {({ focus }) =>
                    option.link ? (
                      <SubmenuIconLink
                        key={`action-menu-${option.name}`}
                        {...option}
                        link={option.link}
                        active={focus}
                        close={close}
                      />
                    ) : (
                      <SubmenuIconButton
                        key={`action-menu-${option.name}`}
                        {...option}
                        active={focus || !!option.active}
                        close={close}
                      />
                    )
                  }
                </MenuItem>
              ))}
            </MenuItems>
          </Transition>
        </div>
      )}
    </Menu>
  );
};
